import { client } from './client';

export interface DietPlan {
  status: 'none' | 'in_progress' | 'done';
  order_id?: number;
  order_type?: string;
  ordered_at?: string;
  wc_status?: string;
  pdf_url?: string | null;
  pdf_order_id?: number | null;
  zalecenia?: string | null;
  meet_link?: string | null;
  slot?: string | null;
  /** UNIX-timestamp слоту (для 15-хв вікна активації кнопки Meet) */
  slot_timestamp?: number | null;
  last_done_plan?: {
    order_id: number;
    pdf_url: string | null;
    zalecenia: string | null;
  };
}

export interface DietCalendarSlot {
  date: string;
  time: string;
  datetime: string;
  label: string;
}

// ── Вага пацієнта ──────────────────────────────────────────────────────────

export interface WeightMeasurement {
  /** Відображувана дата: "08.07" */
  date: string;
  /** ISO дата: "2026-07-08" */
  iso_date: string;
  /** Значення ваги в кг */
  weight: number;
  /** Джерело: з візиту лікаря або ручний вимір пацієнта */
  source: 'visit' | 'manual';
}

export interface WeightHistory {
  measurements: WeightMeasurement[];
  start_weight: number | null;
  current_weight: number | null;
}

export const getWeightHistory = async (): Promise<WeightHistory> => {
  const response = await client.get<WeightHistory>('/diet/weight-history');
  return response.data;
};

export const addWeightMeasurement = async (
  weight: number
): Promise<{ success: boolean; measurement: WeightMeasurement }> => {
  const response = await client.post('/diet/weight', { weight });
  return response.data;
};

// ── Diet plan ──────────────────────────────────────────────────────────────

export const getMyDietPlan = async (): Promise<DietPlan> => {
  const response = await client.get<DietPlan>('/diet/my-plan');
  return response.data;
};

export const getDietCalendarSlots = async (daysAhead = 14): Promise<{ slots: DietCalendarSlot[]; configured: boolean }> => {
  const response = await client.get(`/diet/calendar/slots?days_ahead=${daysAhead}`);
  return response.data;
};

export const submitDietSurvey = async (orderId: number, data: Record<string, any>): Promise<{ success: boolean }> => {
  const response = await client.post(`/diet/orders/${orderId}/survey`, data);
  return response.data;
};

export const downloadDietPdf = async (url: string, filename: string, openInNewTab = false): Promise<void> => {
  let newTab: Window | null = null;
  if (openInNewTab) {
    // Open the new tab synchronously to prevent popup blockers
    newTab = window.open('', '_blank');
  }

  try {
    // Extract path from full URL if needed, client.get expects relative path from baseURL
    const parsedUrl = new URL(url, window.location.origin);
    const path = parsedUrl.pathname.replace('/wp-json/api_front/v1', '');

    const response = await client.get(path, { responseType: 'blob' });
    const blobUrl = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));

    if (openInNewTab && newTab) {
      newTab.location.href = blobUrl;
      // Delay revocation to ensure the new tab has enough time to load the PDF
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 10000);
    } else {
      if (newTab) newTab.close(); // just in case
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.parentNode?.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(blobUrl), 100);
    }
  } catch (err) {
    if (newTab) newTab.close();
    console.error('Failed to download PDF:', err);
    throw err;
  }
};
